/* @flow */

import React from 'react';
import { fetchGraphql, api, gql } from 'utils/ajax';
import type { CallApi } from 'utils/ajax';
// $FlowIgnore
import { handleActions } from 'redux-actions';

import { setMessage } from 'ducks/message';

import {
  fetchAnnotations,
  dataBundlesDetailsQuery,
} from 'ducks/fragments/fragments';

import InternalLink from 'components/Links/InternalLink';

export type GraphqlVariables = { id: string|number }
export type State = { isFetching: bool, id: string, item: Object }
export type Action = {
  type: string,
  payload: Object,
  meta: { id: string }
}

export let DETAILS_REQUEST = 'gdc/DETAILS_REQUEST';
export let DETAILS_SUCCESS = 'gdc/DETAILS_SUCCESS';
export let DETAILS_FAILURE = 'gdc/DETAILS_FAILURE';

export let ADD_TO_BEING_DELETED = 'gdc/ADD_TO_BEING_DELETED';
export let REMOVE_FROM_BEING_DELETED = 'gdc/REOMVE_FROM_BEING_DELETED';

export let DELETE_ENTITY_REQUEST = 'gdc/DELETE_ENTITY_REQUEST';
export let DELETE_ENTITY_SUCCESS = 'gdc/DELETE_ENTITY_SUCCESS';
export let DELETE_ENTITY_FAILURE = 'gdc/DELETE_ENTITY_FAILURE';

export let DEPENDENTS_SUCCESS = 'gdc/DEPENDENTS_SUCCESS';

function entitySpecificQuery(entity) {
  switch (entity) {
  case 'case':
  case 'sample':
  case 'portion':
  case 'analyte':
  case 'aliquot':
  case 'slide':
  case 'read_group':
    return fetchAnnotations;
  case 'clinical_data_bundle':
  case 'biospecimen_data_bundle':
    return dataBundlesDetailsQuery;

  default:
    return '';
  }
}

function queryEntity({
  entity,
  fields,
  id,
  isQCReports,
}) {
  return {
    variables: { id },
    query: `
      query EntityDetailsQuery($id: String) {
        item: ${entity}(id: $id) {
          ${fields.join()}
          ${entitySpecificQuery(entity)}
          ${entity === 'case' ? '' : '_related_cases { id, submitter_id }'}
          ${isQCReports ? '' : '_transaction_logs(order_by_desc: "created_datetime") { id, type created_datetime }'}
        }
      }
    `
  };
}

function queryTransaction({ id }) {
  return {
    variables: { id },
    query:
      `query TransactionDetailsQuery($id: ID) {
        item: transaction_log(id: $id) {
          id
          project_id
          type
          state
          committed_by
          submitter
          is_dry_run
          closed
          created_datetime
          documents {
            id
            name
            doc
            doc_size
            doc_format
            response_json
            response {
              message
              entities {
                id
                unique_keys
                action
                type
                related_cases {
                  id
                  submitter_id
                }
                errors {
                  keys
                  type
                  message
                  dependents {
                    id
                    type
                  }
                }
              }
            }
          }
        }
      }`
  };
}

export function fetchEntity(selected: Object): CallApi {
  return fetchGraphql(
    [{
      type: DETAILS_REQUEST,
      meta: { id: selected.id }
    }, {
      type: DETAILS_SUCCESS,
      meta: { id: selected.id }
    }, DETAILS_FAILURE],
    queryEntity(selected)
  );
}

export function fetchTransaction(selected: Object): CallApi {
  return fetchGraphql(
    [{
      type: DETAILS_REQUEST,
      meta: { id: selected.id },
    }, {
      type: DETAILS_SUCCESS,
      meta: { id: selected.id },
    }, {
      type: DETAILS_FAILURE,
      meta: { id: selected.id },
    }],
    queryTransaction(selected)
  );
}

export function fetchDependents(ids: Array<string>): Function {
  return async dispatch => {
    let { data } = await gql({
      variables: {
        ids,
        first: 1000,
      },
      query: `
        query TransactionDependents(
          $ids: [String]
          $first: Int
        ) {
          node(
            ids: $ids
            first: $first
          ) {
            id
          }
        }`
    });

    dispatch({ type: DEPENDENTS_SUCCESS, payload: data.node.map(x => x.id) });
  };
}

export function deleteEntity(ids: Array<string>, params): Function {
  return async (dispatch, getState) => {
    let { program, project } = params;

    let response = await api({
      method: 'delete',
      url: `${program}/${project}/entities/${ids.join()}?async=true`
    });

    let { transaction_id } = await response.json();

    dispatch({
      type: ADD_TO_BEING_DELETED,
      payload: {
        ids,
        transaction_id,
      }
    });
  };
}

export function onDeleteComplete(transactions: Array<Object>): Function {
  return (dispatch, getState) => {
    let { details: { beingDeleted }} = getState();
    if (beingDeleted.length) {
      transactions.forEach(x => {
        // the id in the response from the sync transaction is a number,
        // but the async one is a string
        let entity = beingDeleted.find(y => y.transaction_id === +x.id);
        if (entity) {
          dispatch({ type: REMOVE_FROM_BEING_DELETED, payload: +x.id });

          if (x.state === 'FAILED') {
            dispatch(setMessage(
              <div>
                <span>Transaction failed.</span>
                <InternalLink
                  to="transactions"
                  withProjectPath
                  query={{ did: x.id }}
                  style={{ color: 'rgb(228, 173, 193)', marginLeft: '0.5rem' }}
                >
                  More Info
                </InternalLink>
              </div>
            ));
          }

          // refresh the page if browsing entities and transaction is successful
          if ((location.pathname.includes('/browse') || location.pathname.includes('/transactions')) &&
            x.state === 'SUCCEEDED'
          ) {
            localStorage.setItem('deleteTransactionSuccess', 'true');
            location.search = '';
          }
        }
      });
    }
  };
}

let initialState = {
  isFetching: false,
  id: '',
  item: {},
  beingDeleted: [],
  dependents: [],
};

export default handleActions({
  [DETAILS_REQUEST]: (state, action) => ({
    ...state,
    isFetching: true,
    id: action.meta.id,
    item: action.meta.id === state.id ? state.item : {},
  }),
  [DETAILS_SUCCESS]: (state, action) => ({
    ...state,
    isFetching: false,
    item: action.meta.id === state.id ? (action.payload.data.item[0] || {}) : state.item,
  }),
  [DETAILS_FAILURE]: (state, action) => ({
    ...state,
    isFetching: false,
    // request could fail, but some data will still be retured
    item: action.meta.id === state.id ? (action.payload.response.data.item[0] || {}) : state.item,
  }),
  [ADD_TO_BEING_DELETED]: (state, action) => ({
    ...state,
    beingDeleted: [...state.beingDeleted, action.payload],
  }),
  [REMOVE_FROM_BEING_DELETED]: (state, action) => ({
    ...state,
    beingDeleted: state.beingDeleted.filter(x => x.transaction_id !== action.payload),
  }),
  [DEPENDENTS_SUCCESS]: (state, action) => ({
    ...state,
    dependents: action.payload,
  }),
}, initialState);
